import { ReactNode, useEffect } from "react";

/** Botones de la pantalla de reunión (mismos colores que el resto de la plataforma: violeta #6D57BC). */
export const meetBtn = {
  primary:
    "rounded-full bg-[#6D57BC] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#5a47a0] disabled:opacity-50",
  secondary:
    "rounded-full border-2 border-[#6D57BC] bg-white px-5 py-2.5 text-sm font-semibold text-[#6D57BC] transition hover:bg-violet-50 disabled:opacity-50",
  danger:
    "rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-50",
  /** Versiones chicas para la barra de la llamada (misma altura que los controles del encabezado) */
  dangerSm:
    "rounded-full bg-red-600 px-4 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-50",
  secondarySm:
    "rounded-full border-2 border-[#6D57BC] bg-white px-4 py-1 text-[13px] font-semibold text-[#6D57BC] transition hover:bg-violet-50 disabled:opacity-50",
  primarySm:
    "rounded-full bg-[#6D57BC] px-4 py-1.5 text-[13px] font-semibold text-white transition hover:bg-[#5a47a0] disabled:opacity-50",
  ghost:
    "rounded-full px-4 py-2.5 text-sm font-medium text-gray-600 underline-offset-2 transition hover:text-gray-900 hover:underline",
};

/**
 * Ventana de aviso de la pantalla de reunión: tarjeta blanca con borde, sombra y esquinas redondeadas (misma estética que el aviso de salida
 * de la plataforma). Se cierra con Escape; `onClose` es el equivalente a "quedarse".
 */
export function MeetModal({
  open,
  onClose,
  title,
  icon,
  children,
  actions,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  actions: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[2001] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`w-full ${wide ? "max-w-xl" : "max-w-md"} rounded-3xl border border-gray-200 bg-white p-7 text-gray-900 shadow-2xl`}
      >
        <div className="mb-4 flex items-start gap-3">
          {icon && <div className="mt-0.5 shrink-0">{icon}</div>}
          <h2 className="text-lg font-bold leading-snug">{title}</h2>
        </div>
        <div className="text-sm leading-relaxed text-gray-700">{children}</div>
        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      </div>
    </div>
  );
}

export const WarningIcon = () => (
  <span
    className="grid h-9 w-9 place-items-center rounded-full bg-amber-100 text-amber-700"
    aria-hidden="true"
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  </span>
);

export const AlertIcon = () => (
  <span
    className="grid h-9 w-9 place-items-center rounded-full bg-red-100 text-red-700"
    aria-hidden="true"
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  </span>
);
